import React from 'react';
import Navigation from './Navigation';
import ToastContainer from './ToastContainer';

/**
 * DarkPageContainer - Immersive dark theme page wrapper
 * Matches the landing page create flow styling
 */
export default function DarkPageContainer({ 
  children, 
  toasts = [], 
  onCloseToast = () => {},
  showNavigation = true,
  showBackgroundImage = true,
  loading = false,
  loadingText = 'Loading...',
  className = '',
  maxWidth = '7xl', // '4xl', '5xl', '6xl', '7xl'
}) {
  const maxWidthClasses = {
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zapt-navy relative overflow-hidden">
        {/* Background elements */}
        {showBackgroundImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{ backgroundImage: 'url(https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/background_image.jpg)' }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-zapt-navy/70 via-zapt-navy/80 to-zapt-navy/70" />
        
        {/* Glows */}
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-zapt-purple rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-zapt-yellow rounded-full blur-3xl opacity-10"></div>
        
        {/* Accent line */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-zapt-purple via-zapt-yellow to-zapt-purple"></div>
        
        {showNavigation && <DarkNavigation />}
        
        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-yellow mx-auto mb-4"></div>
            <p className="text-gray-300">{loadingText}</p>
          </div>
        </div>
        
        <ToastContainer toasts={toasts} onCloseToast={onCloseToast} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zapt-navy relative overflow-hidden">
      {/* Background Image */}
      {showBackgroundImage && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{ backgroundImage: 'url(https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/background_image.jpg)' }}
        />
      )}
      
      {/* Gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-zapt-navy/70 via-zapt-navy/80 to-zapt-navy/70" />
      
      {/* Accent line at top */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-zapt-purple via-zapt-yellow to-zapt-purple"></div>
      
      {/* Glowing orbs */}
      <div className="absolute top-1/4 -left-48 w-96 h-96 bg-zapt-purple rounded-full blur-3xl opacity-20 pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-zapt-yellow rounded-full blur-3xl opacity-10 pointer-events-none"></div>

      {showNavigation && <DarkNavigation />}

      <div className={`relative z-10 ${maxWidthClasses[maxWidth]} mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 ${className}`}>
        {children}
      </div>
      
      <ToastContainer toasts={toasts} onCloseToast={onCloseToast} />
    </div>
  );
}

/**
 * DarkNavigation - Navigation styled for dark backgrounds
 */
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useState } from 'react';
import * as Sentry from '@sentry/browser';
import { useCrmMyAccess } from '@/hooks/queries';

function DarkNavigation() {
  const { user, signOut } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  
  // Check CRM access (admins or salespeople)
  const { data: crmAccess } = useCrmMyAccess();

  const handleSignOut = async () => {
    if (isSigningOut) return;
    
    try {
      setIsSigningOut(true);
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Sign out failed:', error);
      Sentry.captureException(error, {
        extra: { action: 'handleSignOut', location: 'DarkNavigation' }
      });
    } finally {
      setIsSigningOut(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="relative z-20 bg-zapt-navy/50 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/Zapt_White%2BLogo%2B(1).png" 
                alt="ZAPT" 
                className="h-10 w-auto"
              />
            </Link>
          </div>

          <div className="hidden sm:flex sm:items-center sm:space-x-8">
            {(user?.email === 'david@zapt.ai' || user?.email === 'phil@zapt.ai') && (
              <Link
                to="/admin-portal"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  location.pathname.startsWith('/admin-portal') 
                    ? 'text-zapt-yellow bg-zapt-yellow/10' 
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                Admin Portal
              </Link>
            )}
            
            {crmAccess?.canAccessCrm && (
              <Link
                to="/crm"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  location.pathname.startsWith('/crm') 
                    ? 'text-zapt-yellow bg-zapt-yellow/10' 
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                CRM
              </Link>
            )}
            
            {/* Account Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-1 ${
                  location.pathname === '/account'
                    ? 'text-zapt-yellow bg-zapt-yellow/10' 
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                Account
                <svg className={`w-4 h-4 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isAccountMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsAccountMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-xl bg-zapt-navy/95 backdrop-blur-md ring-1 ring-white/20 z-20">
                    <div className="py-1" role="menu">
                      <Link
                        to="/account"
                        onClick={() => setIsAccountMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white"
                      >
                        Account Settings
                      </Link>
                      <div className="border-t border-white/10 my-1" />
                      <button
                        onClick={() => {
                          setIsAccountMenuOpen(false);
                          handleSignOut();
                        }}
                        disabled={isSigningOut}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white disabled:opacity-50"
                      >
                        {isSigningOut ? 'Signing out...' : 'Sign out'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zapt-yellow cursor-pointer"
            >
              <svg
                className="h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="sm:hidden bg-zapt-navy/95 backdrop-blur-md rounded-b-lg border-t border-white/10">
            <div className="pt-2 pb-3 space-y-1">
              
              {(user?.email === 'david@zapt.ai' || user?.email === 'phil@zapt.ai') && (
                <Link
                  to="/admin-portal"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    location.pathname.startsWith('/admin-portal')
                      ? 'text-zapt-yellow bg-zapt-yellow/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Admin Portal
                </Link>
              )}
              
              {crmAccess?.canAccessCrm && (
                <Link
                  to="/crm"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    location.pathname.startsWith('/crm')
                      ? 'text-zapt-yellow bg-zapt-yellow/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  CRM
                </Link>
              )}
              
              <Link
                to="/account"
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive('/account')
                    ? 'text-zapt-yellow bg-zapt-yellow/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Account Settings
              </Link>
            </div>
            
            <div className="pt-4 pb-3 border-t border-white/10">
              <div className="px-3">
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleSignOut();
                  }}
                  disabled={isSigningOut}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-colors duration-200 disabled:opacity-50 cursor-pointer"
                >
                  {isSigningOut ? 'Signing out...' : 'Sign out'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export { DarkNavigation };
